import { Json, JsonObject, JsonPrimitive } from '@se/cp.common';
import { DataBinding, Guid, SimpleDataBindingPart } from '@se/se.ui.html5';
import { forOwn } from 'lodash-es';
import moment, { Moment } from 'moment';
import { CpEntityBase, isEntity } from './cp-entity-base';
import { CpUndoEntity } from './cp-undo-entity';
import { ICpEntity, ICpEntityFactory, PropertyType } from './interfaces';

export class CpEntity extends CpEntityBase implements ICpEntity {
    private readonly properties: Record<string, PropertyType> = {};

    private isCreatedOnClient: boolean;

    get Id(): string {
        if (this.properties.Id) {
            return this.properties.Id.toString();
        }
        if (this.properties.guid) {
            return this.properties.guid.toString();
        }

        return '';
    }

    constructor(factory: ICpEntityFactory, model: JsonObject, path: DataBinding, parent?: ICpEntity) {
        super(factory, path, parent);

        for (const property in model) {
            if (Object.hasOwnProperty.call(model, property)) {
                this.properties[property] = factory.makeEntity(model[property], path.addSimple(property), this);
            }
        }

        if (!this.properties.Id && !this.properties.guid) {
            this.properties.Id = new Guid().toString();
            this.isCreatedOnClient = true;
        } else {
            this.isCreatedOnClient = false;
        }
    }

    // #region CpEntityBase implementation

    forOwn(fn: (key: string, value: PropertyType) => boolean | void): void {
        for (const prop in this.properties) {
            if (Object.hasOwnProperty.call(this.properties, prop) && fn(prop, this.properties[prop])) {
                break;
            }
        }
    }

    /**
     * Recupera tutte le entity in un dato percorso.
     */
    getAllEntities(path: DataBinding): ICpEntity[] | undefined {
        if (!path || path.isEmpty()) {
            return [this];
        }

        const propertyPath = path.root;
        if (!propertyPath.isSimple()) {
            return undefined;
        }
        const propertyValue = this.getProperty(propertyPath);

        if (isEntity(propertyValue)) {
            return propertyValue.getAllEntities(path.next);
        }

        return undefined;
    }

    /**
     * Recupera una entity da un percorso, o la selected entity in caso di lista.
     */
    getSelectedEntity(path: DataBinding, createMissingEntities = false): ICpEntity | undefined {
        if (!path || path.isEmpty()) {
            return this;
        }

        const propertyPath = path.root;
        if (!propertyPath.isSimple()) {
            return undefined;
        }
        const propertyValue = this.getProperty(propertyPath);

        if (isEntity(propertyValue)) {
            return propertyValue.getSelectedEntity(path.next, createMissingEntities);
        }

        if (createMissingEntities && !propertyValue) {
            const newEntity = this.factory.makeEntityFromDataset(this.path.addSimple(propertyPath.value), this);

            this.properties[propertyPath.value] = newEntity;

            if (isEntity(newEntity)) {
                if (path.full.length > 1) {
                    return newEntity.getSelectedEntity(path.next);
                }
                return newEntity;
            }
        }

        return undefined;
    }

    /**
     * Recupera il valore di una diretta proprietà di questa entity.
     */
    getPropertyValue(propertyName: string) {
        return this.properties[propertyName];
    }

    /**
     * Recupera il valore di una diretta proprietà di questa entity come JSON.
     */
    getPropertyPrimitiveValue(propertyName: string) {
        const value = this.getPropertyValue(propertyName);

        if (isEntity(value)) {
            return value.toJSON() as PropertyType;
        }

        return value;
    }

    hasProperty(propertyName: string): boolean {
        return Object.hasOwnProperty.call(this.properties, propertyName);
    }

    /**
     * Imposta il valore di una diretta proprietà di questa entity.
     */
    setPropertyValue(propertyName: string, value: JsonPrimitive | Moment, emitEvent = true) {
        const oldValue = this.getPropertyValue(propertyName);
        if (oldValue === value) {
            return;
        }

        if (isEntity(oldValue)) {
            setTimeout(() => oldValue.destroy(), 0);
        }

        this.properties[propertyName] = value;

        if (emitEvent) {
            this.isModified = true;
            this.select();
        }
    }

    navigateToPath(path: DataBinding, createMissingEntities = false): PropertyType | undefined {
        if (!path || path.isEmpty()) {
            return this;
        }

        const propertyPath = path.root;
        if (!propertyPath.isSimple()) {
            return undefined;
        }
        const propertyValue = this.getProperty(propertyPath);

        if (isEntity(propertyValue)) {
            return propertyValue.navigateToPath(path.next, createMissingEntities);
        }

        if (createMissingEntities && !propertyValue) {
            const newEntity = this.factory.makeEntityFromPlaceholder(this.path.addSimple(propertyPath.value), this);

            this.properties[propertyPath.value] = newEntity;

            if (path.full.length > 1) {
                if (isEntity(newEntity)) {
                    return newEntity.navigateToPath(path.next, createMissingEntities);
                }

                throw new Error(`Property ${path.property?.value} not existing at path ${this.path}.${propertyPath}`);
            }

            return newEntity;
        }

        return propertyValue;
    }

    // #endregion

    // #region ICpEntity implementation

    addEntityToList(path: DataBinding, value: ICpEntity) {
        if (!path || path.isEmpty()) {
            throw new Error(`Entity ${this.path} is not a list.`);
        }

        const propertyPath = path.root;
        if (!propertyPath.isSimple()) {
            return undefined;
        }
        const propertyValue = this.getProperty(propertyPath);

        if (!isEntity(propertyValue)) {
            throw new Error(`Property ${path} is not a list.`);
        }

        propertyValue.addEntityToList(path.next, value);
    }

    setPropertyEntity(propertyName: string, value: JsonPrimitive): CpEntity {
        const binding = this.path.addSimple(propertyName);
        const childEntity = this.factory.makeEntity(value, binding);
        this.setPropertyValue(propertyName, childEntity, true);
        return childEntity as unknown as CpEntity;
    }

    destroy() {
        super.destroy();

        forOwn(this.properties, (property) => {
            if (isEntity(property) && !(property instanceof CpUndoEntity)) {
                property.destroy();
            }
        });
    }

    remove(child?: ICpEntity): boolean {
        if (!child) {
            if (!this.parent) {
                throw new Error('Cannot remove the root entity.');
            }
            return this.parent.remove(this);
        }

        let found = false;

        forOwn(this.properties, (property, name) => {
            if (property === child || (isEntity(property) && property.remove(child))) {
                found = true;
            }
            if (property === child) {
                this.properties[name] = this.factory.makeUndoEntity(property);
            }
        });

        return found;
    }

    select() {
        this.parent?.select(this);
    }

    toJSON(skipAutogeneratedId: boolean = true): JsonObject {
        const result: JsonObject = {};

        for (const propertyName in this.properties) {
            if (Object.hasOwnProperty.call(this.properties, propertyName)) {
                if (
                    skipAutogeneratedId &&
                    propertyName === 'Id' &&
                    (this.isCreatedOnClient || this.properties[propertyName] === null)
                ) {
                    continue;
                }

                result[propertyName] = this.propToJson(this.properties[propertyName]);
            }
        }

        return result;
    }

    // #endregion

    private getProperty(binding: SimpleDataBindingPart): PropertyType | undefined {
        return this.properties[binding.value];
    }

    private propToJson(value: PropertyType): Json {
        if (!value || typeof value !== 'object') {
            return value as JsonPrimitive;
        }

        if (value instanceof Array) {
            return (value as (ICpEntity | JsonPrimitive)[]).map(
                (v) => (v && typeof v === 'object' && 'toJSON' in v ? v.toJSON() : v) as Json,
            );
        }

        if (moment.isMoment(value)) {
            if (value.isValid()) {
                return value.utc(false).toDate().toJSON();
            } else {
                return null;
            }
        }

        return 'toJSON' in value ? value.toJSON() : value;
    }
}
